import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/authStore";
import apiClient from "@/utils/apiClient";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

function validerMotDePasse(motDePasse: string): string | null {
  if (motDePasse.length < 8) {
    return "Le mot de passe doit contenir au moins 8 caractères";
  }

  // Même règle que le backend (au moins une minuscule, une majuscule et un chiffre).
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
  if (!regex.test(motDePasse)) {
    return "Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre";
  }

  return null;
}

type ChangePasswordResponse = {
  message: string;
  token: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: "CLIENT" | "ADMIN" | "TEAM";
    teamType?: "ARCHITECTE" | "PAYSAGISTE" | null;
  };
};

export default function Settings() {
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const titre = useMemo(() => {
    return user?.email ? `Paramètres (${user.email})` : "Paramètres";
  }, [user?.email]);

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Tous les champs sont requis");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (currentPassword === newPassword) {
      setError("Le nouveau mot de passe doit être différent de l'actuel");
      return;
    }

    const erreurMotDePasse = validerMotDePasse(newPassword);
    if (erreurMotDePasse) {
      setError(erreurMotDePasse);
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.post<ChangePasswordResponse>(
        "/auth/change-password",
        {
          currentPassword,
          newPassword,
          confirmPassword,
        },
      );

      const { token, refreshToken, user, message } = response.data;
      login(token, refreshToken, user);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setInfo(message || "Mot de passe mis à jour");
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Impossible de changer le mot de passe",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Button
        asChild
        variant="secondary"
        size="default"
        className="fixed left-4 top-4 z-50 shadow-md border border-slate-200/70 dark:border-slate-700/60 bg-white/95 dark:bg-slate-900/90 backdrop-blur"
      >
        <Link to="/" aria-label="Retour a l'accueil" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          <span>Retour</span>
        </Link>
      </Button>

      <main className="pt-16 px-6 pb-10">
        <div className="max-w-xl mx-auto">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {titre}
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Gérez les paramètres de votre compte.
              </p>
            </div>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Changer le mot de passe</CardTitle>
              <CardDescription>
                Votre nouveau mot de passe doit contenir au moins 8 caractères,
                dont une minuscule, une majuscule et un chiffre.
              </CardDescription>
            </CardHeader>

            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {info && (
                <Alert className="mb-4">
                  <AlertDescription>{info}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={soumettre} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Mot de passe actuel</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="current-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    Confirmer le mot de passe
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <CardFooter className="px-0 pb-0 pt-2">
                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? "Mise à jour..." : "Mettre à jour le mot de passe"}
                  </Button>
                </CardFooter>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
